import React, { ReactNode } from 'react';
import { SubscriptionPlanId, useBillingService } from 'nekst-api';
import { Button, ButtonColor, ButtonSize, ButtonVariant, SimpleBlock, usePopupManager } from 'ui-builder';
import { ConfirmPositiveActionPopup, InformationMessage } from 'features/nekst-widgets';
import { LockInBannerToggleWrapper } from 'feature-toggle';
import { useBillingContext } from './BillingPageContext';
import styles from './BillingSettings.module.scss';

function buildLockInConfirmationMessage(
  planId: SubscriptionPlanId | undefined,
  lockInMonthlyRate: number,
  lockInAnnualPrice: number,
  postLockInMonthlyPrice: number,
  isLegacy: boolean,
): ReactNode {
  const isTeamPro = planId === SubscriptionPlanId.TEAM_PRO;
  const planName = isTeamPro ? 'Team Pro' : 'Solo Pro';

  const rateIntro = isLegacy
    ? <>You are about to lock in at the <strong>reduced</strong> {planName} rate of ${lockInMonthlyRate.toLocaleString()}/mo for 12 months.</>
    : <>You are about to lock in your monthly rate of ${lockInMonthlyRate.toLocaleString()}/mo for 12 months.</>;

  const seatNote = isTeamPro
    ? <> There will be no change in pricing for your existing Administrator/Team Member seats.</>
    : null;

  return (
    <>
      {rateIntro}
      {' '}You will be billed ${lockInAnnualPrice.toLocaleString()} upon acceptance of this offer.
      {' '}Any remaining time on your current plan will be applied as a credit to the amount being charged.
      <br /><br />
      After the lock-in period expires, your rate will change to ${postLockInMonthlyPrice.toLocaleString()}/mo.
      {seatNote}
    </>
  );
}

function LockInBannerContent() {
  const billingContext = useBillingContext();
  const billingData = billingContext.getData();
  const teamBilling = billingData.teamBilling;
  const billingService = useBillingService();
  const popupManager = usePopupManager();

  if (!teamBilling.isLockInEligible || !teamBilling.lockInOffer) {
    return null;
  } else {
    const { lockInMonthlyRate, lockInAnnualPrice, postLockInMonthlyPrice, isLegacy } = teamBilling.lockInOffer;

    const confirmationMessage = buildLockInConfirmationMessage(
      teamBilling.planId,
      lockInMonthlyRate,
      lockInAnnualPrice,
      postLockInMonthlyPrice,
      isLegacy,
    );

    const handleLockIn = () => {
      popupManager.openPopup(
        <ConfirmPositiveActionPopup
          title="Lock In Your Rate"
          message={confirmationMessage}
          buttonText="Confirm Lock-In"
          submitFormFunc={async () => {
            await billingService.createLockIn();
            billingContext.refresh();
          }}
        />,
      );
    };

    return (
      <InformationMessage>
        <strong>Lock in your current rate!</strong>
        {' '}
        Lock in at ${lockInMonthlyRate.toLocaleString()}/mo
        for 12 months. After the lock-in period, your rate will transition to ${postLockInMonthlyPrice.toLocaleString()}/mo.
        This offer is available until April 30, 2026.
        <SimpleBlock className={styles.lockInButton}>
          <Button
            text="Lock In My Rate for 12 Months"
            onClick={handleLockIn}
            color={ButtonColor.GREEN}
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.FILLED}
          />
        </SimpleBlock>
      </InformationMessage>
    );
  }
}

export default function LockInBanner() {
  return (
    <LockInBannerToggleWrapper>
      <LockInBannerContent />
    </LockInBannerToggleWrapper>
  );
}
